import { DeadlineStates } from './enums/deadline-state';

/**
 * Représente le status d'une étape par rapport à sa date limite.
 */
export class WorkflowStepDeadlineStatus {
    /**
     * Texte affichant le temps restant avant la date limite ou le temps passé depuis la date limite en cas de retard.
     */
    public remainingTime: string;

    /**
     * Status de l'étape par rapport à la date limite.
     */
    public status: DeadlineStates;

    /**
     * Texte affichant le libellé du status de l'étape par rapport à la date limite.
     */
    public statusLabel: string;

    /**
     * Texte affichant le libellé du temps restant.
     */
    public get remainingTimeLabel(): string {
        if (this.isStepCompleted) {
            return 'Etape terminée';
        } else if (this.isStepLate) {
            return "Temps passé depuis l'échéance";
        } else {
            return 'Temps restant avant échéance';
        }
    }

    /**
     * Valeur indiquant si l'étape est en retard.
     */
    private isStepLate: boolean = null;

    /**
     * Constructeur
     * @param remainingTimeBeforeDeadline : Temps restant avant la date limite exprimé en minutes. Une valeur négative indique que la date limite est dépassé depuis le nombre de minutes spécifiées.
     */
    public constructor(
        public remainingTimeBeforeDeadline: number = null,
        public stepMaxDuration: number = null,
        public isStepCompleted: boolean = null
    ) {
        if (remainingTimeBeforeDeadline) {
            this.isStepLate = remainingTimeBeforeDeadline < 0;
            this.remainingTime = this.formatTime(Math.abs(remainingTimeBeforeDeadline));

            if (this.isStepLate) {
                this.status = DeadlineStates.DeadlinePassed;
                this.statusLabel = `Délai dépassé: Retard de ${this.remainingTime}`;
            } else if ((remainingTimeBeforeDeadline * 100) / stepMaxDuration <= 20) {
                // TODO : remplacer la valeur 20 par un paramètre
                this.status = DeadlineStates.NearDeadline;
                this.statusLabel = `Proche de la date limite. Temps restant : ${this.remainingTime}`;
            } else {
                this.status = DeadlineStates.FarFromDeadline;
                this.statusLabel = 'Délai respecté';
            }

            if (isStepCompleted) {
                // Si l'étape est terminé, le nombre de jour avant échéance n'a pas de raison d'être affiché.
                this.remainingTime = '';
            }
        }
    }

    public static get Undefined(): WorkflowStepDeadlineStatus {
        return new WorkflowStepDeadlineStatus();
    }

    private formatTime(time: number): string {
        if (!time || time === 0) {
            return '';
        }

        const minuts = time % 60;
        const totalHours = Math.floor(time / 60);
        const remainingHours = totalHours % 24;
        const totalDays = Math.floor(totalHours / 24);
        const remainingDays = totalDays % 7;
        const weeks = Math.floor(totalDays / 7);

        let format: string = '';

        if (weeks > 0) {
            format += `${weeks} semaine(s)`;
        }

        if (remainingDays > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${remainingDays} jour(s)`;
        }

        if (remainingHours > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${remainingHours} heure(s)`;
        }

        if (minuts > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${minuts} minute(s)`;
        }

        return format;
    }
}
